// src/components/AddressSearchPanel.jsx
/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Stack,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { geocodeByAddr, reverseGeocode } from 'features/geocoder/services/geocodeApi';
import { useGeoCodingContext } from 'features/geocoder/context/GeoCodingContext';
import { transform } from 'ol/proj';
import * as geoConfig from 'config/geocoder/config';
import { style as s } from 'features/userData/style';
import { colors } from 'features/userData/style';
import RunAndDownloadButtons from './common/RunAndDownloadButtons';
import SearchResultArea from './SearchResultArea';

const AddressSearchPanel = ({
    geocodingTypeOptions = [],
    coordSystemOptions = [],
    onResultRecord = undefined,
    downloadOptions = [],
    showReverse,
    setShowReverse
}) => {
    const { geocodingType, setGeocodingType, coordSystem, setCoordSystem } = useGeoCodingContext();
    const CONTROL_HEIGHT = geoConfig.CONTROL_HEIGHT;

    // 지오코딩(주소→좌표)
    const [address, setAddress] = useState('');
    const [result, setResult] = useState({ x: '', y: '', x3857: '', y3857: '' });

    // 리버스 지오코딩(좌표→주소)
    const [revX, setRevX] = useState('');
    const [revY, setRevY] = useState('');
    const [revRoadAddr, setRevRoadAddr] = useState('');
    const [revJibunAddr, setRevJibunAddr] = useState('');

    // 검색 성공 여부
    const [hasSuccessfulSearchResult, setHasSuccessfulSearchResult] = useState(false);

    // 탭 변경 시 입력값, 검색 성공 초기화(다운로드 버튼 비활성화)
    useEffect(() => {
        setHasSuccessfulSearchResult(false);

        if (showReverse) {
            // Reverse 모드 (좌표 검색)로 전환: Forward 모드의 결과 값 초기화
            setResult({ x: '', y: '', x3857: '', y3857: '' });
        } else {
            // Forward 모드 (주소 검색)로 전환: Reverse 모드의 결과 값 초기화
            setRevRoadAddr('');
            setRevJibunAddr('');
        }
    }, [showReverse]);
 
    // 공용: 숫자 정규화 헬퍼
    const toNum = (v) => {
        if (v === '' || v == null) return null;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : null;
    };

    const getMapCoordinates = (rawX, rawY, fallbackX3857, fallbackY3857) => {
        const fallbackX = toNum(fallbackX3857);
        const fallbackY = toNum(fallbackY3857);
        if (fallbackX != null && fallbackY != null) {
            return [fallbackX, fallbackY];
        }
        const parsedX = toNum(rawX);
        const parsedY = toNum(rawY);
        if (parsedX == null || parsedY == null) return [null, null];
        const sridCode = coordSystem?.name ? `EPSG:${coordSystem.name}` : 'EPSG:4326';
        if (sridCode === 'EPSG:3857') {
            return [parsedX, parsedY];
        }
        try {
            const [mx, my] = transform([parsedX, parsedY], sridCode, 'EPSG:3857');
            if (Number.isFinite(mx) && Number.isFinite(my)) {
                return [mx, my];
            }
        } catch (err) {
            // ignore projection errors
        }
        return [null, null];
    };

    // 지오코딩: 주소 → 좌표
    const handleQuery = async () => {
        if (!address) return;

        try {
            const data = await geocodeByAddr({
                address,
                addrType: geocodingType.name,
                srid: coordSystem.name, // 공통 좌표계 사용
            });
            if (!data) {
                setHasSuccessfulSearchResult(false);
                setResult({ x: '', y: '', x3857: '', y3857: '' });
                alert('검색결과가 없습니다.');
                return;
            }
            const x = data.x;
            const y = data.y;
            const [mapX, mapY] = getMapCoordinates(x, y, data.x3857, data.y3857);

            setResult({ x, y, x3857: mapX, y3857: mapY });
            setHasSuccessfulSearchResult(true);
            console.log(data);
            onResultRecord?.({
                id: Date.now(),
                mode: 'forward',
                input: address,
                addrType: geocodingType?.name,
                srid: coordSystem?.name,
                sridLabel: coordSystem?.value,
                x: Number(x),
                y: Number(y),
                x3857: mapX ?? undefined,
                y3857: mapY ?? undefined,
                roadAddress: data.roadAddress ?? '',
                jibunAddress: data.jibunAddress ?? '',
                timestamp: new Date().toISOString(),
                fileName: null,
                source: 'manual',
            }, mapX ?? undefined, mapY ?? undefined);
        } catch (e) {
            setHasSuccessfulSearchResult(false);
            setResult({ x: '', y: '', x3857: '', y3857: '' });
            alert('죄송합니다.\n해당 주소는 변환할 수 없습니다.');
        }
    };

    // 리버스 지오코딩: 좌표 → 주소
    const handleReverse = async () => {
        const xNum = toNum(revX);
        const yNum = toNum(revY);
        if (xNum == null || yNum == null) {
            alert('유효한 X, Y 좌표를 입력해 주세요.');
            return;
        }
        try {
            const data = await reverseGeocode({
                x: xNum,
                y: yNum,
                addrType: geocodingType.name,
                srid: coordSystem.name,
            });
            if (!data) {
                alert('주소를 찾지 못했습니다.');
                setHasSuccessfulSearchResult(false);
                setRevRoadAddr('');
                setRevJibunAddr('');
                return;
            }

            setRevRoadAddr(data.roadaddress || '');
            setRevJibunAddr(data.jibunaddress || '');
            setHasSuccessfulSearchResult(true);

            const xVal = data.x ?? xNum;
            const yVal = data.y ?? yNum;
            const [mapX, mapY] = getMapCoordinates(xVal, yVal, data.x3857, data.y3857);
            onResultRecord?.({
                id: Date.now(),
                mode: 'reverse',
                input: `${xNum}, ${yNum}`,
                addrType: geocodingType?.name,
                srid: coordSystem?.name,
                sridLabel: coordSystem?.value,
                x: Number(xVal),
                y: Number(yVal),
                x3857: mapX ?? undefined,
                y3857: mapY ?? undefined,
                roadAddress: data.roadaddress || data.roadAddress || '',
                jibunAddress: data.jibunaddress || data.jibunAddress || '',
                timestamp: new Date().toISOString(),
                fileName: null,
                source: 'manual',
            }, mapX ?? undefined, mapY ?? undefined);
        } catch (e) {
            setHasSuccessfulSearchResult(false);
            alert('주소 변환 중 오류가 발생했습니다.');
        }
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            height: CONTROL_HEIGHT,
        },
        '& .MuiOutlinedInput-input': {
            py: 0,
        },
    };
    const [forwardDownload, setForwardDownload] = useState('');
    const [reverseDownload, setReverseDownload] = useState('');

    const handleDownloadSelect = (value, mode) => {
        const option = downloadOptions.find((opt) => opt.value === value);
        option?.action?.();
        if (mode === 'forward') {
            setForwardDownload('');
        } else {
            setReverseDownload('');
        }
    };

    const handleDownloadOptions = () => {
        let isFieldEmpty = false;
        
        if (showReverse) isFieldEmpty = revX.trim().length === 0 || revY.trim().length === 0;
        else isFieldEmpty = address.trim().length === 0

        return isFieldEmpty || !hasSuccessfulSearchResult ? [] : downloadOptions;
    };

    // MenuItem 클릭 핸들러
    const handleDownloadItemClick = (fileFormat) => {
        const mode = showReverse ? 'reverse' : 'forward'; 
        handleDownloadSelect(fileFormat, mode); 
    };

    const renderCommonOptions = () => (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            <FormControl
                size="small"
                sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        py: 0,
                    },
                }}
            >
                <Stack
                    direction="row" 
                    alignItems="center"
                    spacing={0.5} 
                    sx={{
                        mb: 0.5,
                    }}
                >
                    <Typography
                        variant="body2" 
                        sx={geoConfig.inputLabelSx}
                    >
                        좌표계
                    </Typography>
                    <Tooltip 
                        title={"좌표계 툴팁"}
                        placement="right"
                        arrow
                    >
                        <HelpOutlineIcon 
                            fontSize="small" 
                            sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                            }} 
                        />
                    </Tooltip>
                </Stack>
                
                <Select
                    size="small"
                    value={coordSystem.value}
                    onChange={(e) =>
                        setCoordSystem(coordSystemOptions.find((o) => o.value === e.target.value))
                    }
                >
                    {coordSystemOptions.map((opt) => (
                        <MenuItem key={opt.name} value={opt.value}>{opt.value}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl
                size="small"
                sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT },
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        py: 0,
                    },
                }}
            >
                <Stack
                    direction="row" 
                    alignItems="center"
                    spacing={0.5} 
                    sx={{
                        mb: 0.5,
                    }}
                >
                    <Typography
                        variant="body2" 
                        sx={geoConfig.inputLabelSx}
                    >
                        주소 유형
                    </Typography>
                    <Tooltip 
                        title={"주소 유형 툴팁"}
                        placement="right"
                        arrow
                    >
                        <HelpOutlineIcon 
                            fontSize="small" 
                            sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                            }} 
                        />
                    </Tooltip>
                </Stack>
                <Select
                    size="small"
                    value={geocodingType.value}
                    onChange={(e) =>
                        setGeocodingType(geocodingTypeOptions.find((o) => o.value === e.target.value))
                    }
                    // label="지오코딩 유형"
                >
                    {geocodingTypeOptions.map((opt) => (
                        <MenuItem key={opt.name} value={opt.value}>{opt.value}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );

    const forwardPanel = (
        <div>
            <Stack>
                {renderCommonOptions()}
                <Stack
                    direction="row" 
                    alignItems="center"
                    spacing={0.5} 
                    sx={{
                        mt: '13px',
                        mb: 0.5,
                    }}
                >
                    <Typography
                        variant="body2" 
                        sx={geoConfig.inputLabelSx}
                    >
                        주소
                    </Typography>
                    <Tooltip 
                        title={"검색이 잘 되지 않는 경우 시도명을 포함하여 입력해 주세요."}
                        placement="right"
                        arrow
                    >
                        <HelpOutlineIcon 
                            fontSize="small" 
                            sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                            }} 
                        />
                    </Tooltip>
                </Stack>
                <TextField
                    placeholder="경상남도 진주시 충의로 24"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
                    sx={inputSx}
                    inputProps={{
                        autoComplete: 'off',
                    }}
                />
            </Stack>
            
            <RunAndDownloadButtons 
                showReverse={showReverse}
                handleQuery={handleQuery}
                downloadOptions={handleDownloadOptions()}
                address={address}
                handleDownloadItemClick={handleDownloadItemClick}
                startIconName="SearchIcon"
                panelMode="1"
            />

            {result?.x && (
                <SearchResultArea 
                    showReverse={showReverse}
                    revRoadAddr={revRoadAddr}
                    revJibunAddr={revJibunAddr}
                    coord={result}
                />
            )}
        </div>
    );

    const reversePanel = (
        <div>
            <Stack>
                {renderCommonOptions()}
                <Typography
                    variant="body2" 
                    sx={[geoConfig.inputLabelSx, {mt: '13px'}]}
                >
                    X 좌표
                </Typography>
                <TextField
                    // label="X 좌표"
                    placeholder="X 좌표를 입력하세요."
                    variant="outlined"
                    size="small"
                    value={revX}
                    onChange={(e) => setRevX(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleReverse()}
                    sx={inputSx}
                />
                <Typography
                    variant="body2" 
                    sx={[geoConfig.inputLabelSx, {mt: '13px'}]}
                >
                    Y 좌표
                </Typography>
                <TextField
                    // label="Y 좌표"
                    placeholder="Y 좌표를 입력하세요."
                    variant="outlined"
                    size="small"
                    value={revY}
                    onChange={(e) => setRevY(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleReverse()}
                    sx={inputSx}
                />
            </Stack>

            <RunAndDownloadButtons 
                showReverse={showReverse}
                handleQuery={handleReverse}
                downloadOptions={handleDownloadOptions()}
                revX={revX}
                revY={revY}
                handleDownloadItemClick={handleDownloadItemClick}
                startIconName="SearchIcon"
                panelMode="1"
            />

            {(revRoadAddr || revJibunAddr) && (
                <SearchResultArea 
                    showReverse={showReverse}
                    revRoadAddr={revRoadAddr}
                    revJibunAddr={revJibunAddr}
                    coord={result}
                />
            )}
        </div>
    );

    return (
        <>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={showReverse ? 'reverse' : 'forward'}
                onChange={(_, v) => {
                    if (!v) return;
                    setShowReverse(v === 'reverse');
                }}
                sx={{
                    height: geoConfig.CONTROL_HEIGHT,
                    width: '100%',
                    mt: 2,
                    mb: 3.5,
                    '& .MuiToggleButton-root': { 
                        flex: 1, 
                        px: 1.5, 
                        color: '#323232', 
                        fontWeight: 500,
                        fontSize: '14px' 
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                        bgcolor: geoConfig.themeColor,
                        color: '#fff',
                        '&:hover': { bgcolor: colors.primaryBlue },
                    },
                }}
            >
                <ToggleButton value="forward">주소로 좌표 검색</ToggleButton>
                <ToggleButton value="reverse">좌표로 주소 검색</ToggleButton>
            </ToggleButtonGroup>
            <Box>
                {showReverse ? (
                    reversePanel
                ) : (
                    forwardPanel
                )}
            </Box>
        </>
    );
};

AddressSearchPanel.propTypes = {
    geocodingTypeOptions: PropTypes.array,
    coordSystemOptions: PropTypes.array,
    onResultRecord: PropTypes.func,
    downloadOptions: PropTypes.array,
};

export default AddressSearchPanel;